import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

// Mock data for forum categories
const forumCategories = [
  {
    id: 'general',
    name: 'General Discussion',
    description: 'General kratom discussion and community chat',
    icon: '💬',
    color: '#00d166',
    threadCount: 1247,
    postCount: 15832,
    lastActivity: {
      threadId: 'thread1',
      threadTitle: 'Welcome to the community!',
      userId: 'user1',
      username: 'KratomExpert',
      timestamp: new Date('2024-12-08T10:30:00Z')
    },
    moderators: ['mod1'],
    isLocked: false,
    order: 1
  },
  {
    id: 'vendor-discussion',
    name: 'Vendor Discussion',
    description: 'Discuss vendors, experiences, and recommendations',
    icon: '🏪',
    color: '#4299e1',
    threadCount: 892,
    postCount: 12045,
    lastActivity: {
      threadId: 'thread2',
      threadTitle: 'Best vendors for red strains?',
      userId: 'user2',
      username: 'RedStrainLover',
      timestamp: new Date('2024-12-08T09:15:00Z')
    },
    moderators: ['mod1', 'mod2'],
    isLocked: false,
    order: 2
  },
  {
    id: 'strain-reviews',
    name: 'Strain Reviews',
    description: 'Share detailed strain reviews and experiences',
    icon: '🌿',
    color: '#48bb78',
    threadCount: 2156,
    postCount: 18903,
    lastActivity: {
      threadId: 'thread3',
      threadTitle: 'Red Maeng Da - Detailed Review',
      userId: 'user3',
      username: 'StrainReviewer',
      timestamp: new Date('2024-12-08T08:45:00Z')
    },
    moderators: ['mod2'],
    isLocked: false,
    order: 3
  },
  {
    id: 'dosing-help',
    name: 'Dosing & Effects',
    description: 'Questions about dosing, effects, and safety',
    icon: '⚗️',
    color: '#ed8936',
    threadCount: 634,
    postCount: 7892,
    lastActivity: {
      threadId: 'thread4',
      threadTitle: 'Tapering schedule advice needed',
      userId: 'user4',
      username: 'TaperingHelp',
      timestamp: new Date('2024-12-08T07:20:00Z')
    },
    moderators: ['mod1'],
    isLocked: false,
    order: 4
  },
  {
    id: 'verified-vendors',
    name: 'Verified Vendor Zone',
    description: 'Exclusive area for verified vendors to interact with customers',
    icon: '✅',
    color: '#9f7aea',
    threadCount: 234,
    postCount: 1567,
    lastActivity: {
      threadId: 'thread5',
      threadTitle: 'New batch of Green Malay available',
      userId: 'vendor1',
      username: 'PremiumKratom',
      timestamp: new Date('2024-12-08T06:00:00Z')
    },
    moderators: ['mod1'],
    isLocked: false,
    requiredRole: 'verified_vendor',
    order: 5
  },
  {
    id: 'trading-post',
    name: 'Trading Post',
    description: 'Buy, sell, and trade kratom products with other users',
    icon: '🤝',
    color: '#38b2ac',
    threadCount: 445,
    postCount: 2134,
    lastActivity: {
      threadId: 'thread6',
      threadTitle: '[WTS] 500g Green Dragon - $45 shipped',
      userId: 'user5',
      username: 'KratomTrader',
      timestamp: new Date('2024-12-08T05:30:00Z')
    },
    moderators: ['mod2'],
    isLocked: false,
    order: 6
  },
  {
    id: 'off-topic',
    name: 'Off Topic',
    description: 'Non-kratom discussions and general chat',
    icon: '🎭',
    color: '#718096',
    threadCount: 567,
    postCount: 8921,
    lastActivity: {
      threadId: 'thread7',
      threadTitle: 'What are you listening to right now?',
      userId: 'user6',
      username: 'MusicLover',
      timestamp: new Date('2024-12-08T04:15:00Z')
    },
    moderators: ['mod1'],
    isLocked: false,
    order: 7
  }
]

const recentActivity = [
  {
    id: '1',
    type: 'thread',
    title: 'Best morning strains for energy?',
    author: 'EnergySeeker',
    authorRole: 'member' as const,
    category: 'General Discussion',
    timestamp: new Date('2024-12-08T11:30:00Z'),
    replies: 23,
    views: 156
  },
  {
    id: '2',
    type: 'post',
    title: 'Re: Red Dragon effects review',
    author: 'PremiumKratom',
    authorRole: 'verified_vendor' as const,
    category: 'Strain Reviews',
    timestamp: new Date('2024-12-08T11:15:00Z'),
    replies: 0,
    views: 45
  },
  {
    id: '3',
    type: 'thread',
    title: 'Tapering success story - 2 months clean!',
    author: 'RecoveryJourney',
    authorRole: 'trusted' as const,
    category: 'Dosing & Effects',
    timestamp: new Date('2024-12-08T10:45:00Z'),
    replies: 18,
    views: 234
  }
]

const onlineUsers = [
  { id: '1', username: 'admin', role: 'admin' as const },
  { id: '2', username: 'redbeard', role: 'trusted' as const },
  { id: '3', username: 'kilt', role: 'verified_vendor' as const },
  { id: '4', username: 'botanicals', role: 'verified_vendor' as const },
  { id: '5', username: 'trusteduser', role: 'trusted' as const },
  { id: '6', username: 'demo', role: 'member' as const }
]

function formatTimestamp(date: Date): string {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  return `${days}d ago`
}

function getRoleColor(role: string): string {
  switch (role) {
    case 'admin': return '#e53e3e'
    case 'moderator': return '#9f7aea'
    case 'verified_vendor': return '#00d166'
    case 'trusted': return '#4299e1'
    default: return '#a0aec0'
  }
}

function getRoleBadge(role: string): string {
  switch (role) {
    case 'admin': return '👑'
    case 'moderator': return '🛡️'
    case 'verified_vendor': return '✅'
    case 'trusted': return '⭐'
    default: return ''
  }
}

export default function Community() {
  const { user, isLoggedIn } = useAuth()
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  const canAccessCategory = (category: any) => {
    if (!category.requiredRole) return true
    if (!isLoggedIn) return false
    return user?.role === category.requiredRole || user?.role === 'moderator' || user?.role === 'admin'
  }

  return (
    <div className="community-page">
      <div className="container">
        {/* Community Header */}
        <section className="community-header">
          <h1>Community Forum</h1>
          <p className="community-subtitle">
            Connect with fellow kratom enthusiasts, share experiences, and learn from the community
          </p>
          
          <div className="community-stats">
            <div className="stat">
              <span className="stat-number">6,175</span>
              <span className="stat-label">Total Threads</span>
            </div>
            <div className="stat">
              <span className="stat-number">65,294</span>
              <span className="stat-label">Total Posts</span>
            </div>
            <div className="stat">
              <span className="stat-number">2,847</span>
              <span className="stat-label">Members</span>
            </div>
            <div className="stat">
              <span className="stat-number">{onlineUsers.length}</span>
              <span className="stat-label">Online Now</span>
            </div>
          </div>

          {isLoggedIn && (
            <div className="quick-actions">
              <Link to="/community/create-thread" className="action-button primary">
                📝 New Thread
              </Link>
              <Link to="/community/feed" className="action-button secondary">
                🏠 Activity Feed
              </Link>
              <Link to="/community/my-posts" className="action-button secondary">
                📋 My Posts
              </Link>
            </div>
          )}
        </section>

        <div className="community-layout">
          {/* Main Forum Categories */}
          <div className="forum-categories">
            <h2>Forum Categories</h2>
            
            {forumCategories.map((category) => (
              <div 
                key={category.id} 
                className={`category-card ${!canAccessCategory(category) ? 'locked' : ''}`}
                style={{ borderLeftColor: category.color }}
              >
                <div className="category-info">
                  <div className="category-header">
                    <span className="category-icon" style={{ color: category.color }}>
                      {category.icon}
                    </span>
                    <div className="category-details">
                      <h3 className="category-name">
                        <Link 
                          to={canAccessCategory(category) ? `/community/category/${category.id}` : '#'}
                          className={!canAccessCategory(category) ? 'disabled' : ''}
                        >
                          {category.name}
                        </Link>
                        {category.requiredRole && (
                          <span className="role-requirement">
                            {getRoleBadge(category.requiredRole)} Verified Vendors Only
                          </span>
                        )}
                      </h3>
                      <p className="category-description">{category.description}</p>
                    </div>
                  </div>
                  
                  <div className="category-stats">
                    <span className="stat">
                      <strong>{category.threadCount.toLocaleString()}</strong> threads
                    </span>
                    <span className="stat">
                      <strong>{category.postCount.toLocaleString()}</strong> posts
                    </span>
                  </div>
                </div>

                {category.lastActivity && canAccessCategory(category) && (
                  <div className="last-activity">
                    <p className="activity-thread">
                      <Link to={`/community/thread/${category.lastActivity.threadId}`}>
                        {category.lastActivity.threadTitle}
                      </Link>
                    </p>
                    <p className="activity-meta">
                      by{' '}
                      <Link 
                        to={`/profile/${category.lastActivity.userId}`}
                        className="username"
                      >
                        {category.lastActivity.username}
                      </Link>
                      {' '}{formatTimestamp(category.lastActivity.timestamp)}
                    </p>
                  </div>
                )}

                {!canAccessCategory(category) && (
                  <div className="locked-message">
                    <span className="lock-icon">🔒</span>
                    <p>This category requires special permissions</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Sidebar */}
          <div className="community-sidebar">
            {/* Recent Activity */}
            <div className="sidebar-section">
              <h3>Recent Activity</h3>
              <div className="activity-list">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="activity-item">
                    <div className="activity-header">
                      <span className="activity-type">
                        {activity.type === 'thread' ? '📝' : '💬'}
                      </span>
                      <Link to={`/community/thread/${activity.id}`} className="activity-title">
                        {activity.title}
                      </Link>
                    </div>
                    <div className="activity-meta">
                      <span 
                        className="author-name"
                        style={{ color: getRoleColor(activity.authorRole) }}
                      >
                        {getRoleBadge(activity.authorRole)} {activity.author}
                      </span>
                      <span className="activity-time">
                        {formatTimestamp(activity.timestamp)}
                      </span>
                    </div>
                    <div className="activity-stats">
                      <span>{activity.replies} replies</span>
                      <span>{activity.views} views</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Online Users */}
            <div className="sidebar-section">
              <h3>Online Now ({onlineUsers.length})</h3>
              <div className="online-users">
                {onlineUsers.map((user) => (
                  <div key={user.id} className="online-user">
                    <span className="online-indicator">🟢</span>
                    <Link 
                      to={`/profile/${user.id}`}
                      className="username"
                      style={{ color: getRoleColor(user.role) }}
                    >
                      {getRoleBadge(user.role)} {user.username}
                    </Link>
                  </div>
                ))}
              </div>
            </div>

            {/* Community Guidelines */}
            <div className="sidebar-section">
              <h3>Community Guidelines</h3>
              <div className="guidelines">
                <ul>
                  <li>Be respectful and kind to all members</li>
                  <li>No spam or self-promotion without permission</li>
                  <li>Keep discussions kratom-related in main categories</li>
                  <li>Follow all applicable laws and regulations</li>
                  <li>Report inappropriate content to moderators</li>
                </ul>
                <Link to="/community/rules" className="view-rules">
                  View Full Rules
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}